/**
 * H.264 reference-chain check for the WHEP video receiver. No browser APIs, so it runs in the worker and in tests.
 *
 * Every reference picture after an IDR carries the previous one's frame_num plus one. A skip means a frame never
 * arrived, and everything after it decodes against the wrong reference until the next IDR. The browser cannot see
 * such a loss when it happened before the media server: the server re-packetizes with its own, continuous RTP
 * sequence numbers. Reading frame_num from the slice header is the only way to notice it.
 *
 * Ported as is from the WHEP debug viewer (index_new.html, FrameInspector), without its SEI timestamp.
 */

// Bit-level parsing of the H.264 headers.
/* eslint-disable no-bitwise */

/**
 * What one frame means for the decoder.
 *   ok: decodes cleanly, or nothing to check it against (no SPS seen)
 *   wait: no IDR yet, since the start or since a break
 *   gap: this frame broke the chain, or its headers could not be parsed
 *   noslice: no picture data at all (a lone SEI when the slice packets were lost)
 */
export type ChainState = 'ok' | 'wait' | 'gap' | 'noslice';

// NAL unit types. SEI is skipped on purpose: the debug viewer reads its sender timestamp, the app has no use for it.
const SLICE_TYPES = [1, 5];
const IDR = 5;
const SPS = 7;
// Profiles whose SPS carries chroma_format_idc, bit depths and scaling lists before log2_max_frame_num_minus4.
const HIGH_PROFILES = [100, 110, 122, 244, 44, 83, 86, 118, 128, 138, 139, 134, 135];
// Enough of a slice header to reach frame_num: first_mb_in_slice, slice_type, pps_id and up to 16 bits of frame_num.
const SLICE_HEADER_BYTES = 32;

interface Nal {
  type: number;
  refIdc: number;
  /** Just past the NAL header byte. */
  start: number;
  end: number;
}

interface Sps {
  frameNumBits: number;
  separateColourPlane: boolean;
}

/** RBSP bytes b[start, end) with the emulation prevention bytes removed, read as bits. */
class Bits {
  private readonly d: number[] = [];
  private p = 0;

  constructor(b: Uint8Array, start: number, end: number) {
    let zeros = 0;
    for (let i = start; i < end; i++) {
      if (zeros >= 2 && b[i] === 3) {
        zeros = 0;
      } else {
        this.d.push(b[i]);
        zeros = b[i] === 0 ? zeros + 1 : 0;
      }
    }
  }

  u(n: number): number {
    let v = 0;
    for (let i = 0; i < n; i++) {
      const byte = this.d[this.p >> 3];
      if (byte === undefined) {
        throw new Error('short rbsp');
      }
      v = v * 2 + ((byte >> (7 - (this.p & 7))) & 1);
      this.p += 1;
    }
    return v;
  }

  ue(): number {
    let zeros = 0;
    while (this.u(1) === 0) {
      zeros += 1;
      if (zeros > 31) {
        throw new Error('bad exp-golomb');
      }
    }
    return 2 ** zeros - 1 + this.u(zeros);
  }

  se(): number {
    const k = this.ue();
    return k % 2 === 1 ? (k + 1) / 2 : -(k / 2);
  }
}

/**
 * The NAL units of an Annex B frame up to and including the first slice. The slice data is the bulk of the frame
 * and is never scanned.
 */
function headerNals(b: Uint8Array): Nal[] {
  const nals: Nal[] = [];
  let i = 0;
  while (i + 3 < b.length) {
    if (b[i] === 0 && b[i + 1] === 0 && b[i + 2] === 1) {
      const h = i + 3;
      if (nals.length > 0) {
        nals[nals.length - 1].end = i;
      }
      const type = b[h] & 0x1f;
      nals.push({ type, refIdc: (b[h] >> 5) & 3, start: h + 1, end: b.length });
      if (SLICE_TYPES.includes(type)) {
        break;
      }
      i = h;
    } else {
      i += 1;
    }
  }
  return nals;
}

function skipScalingList(bits: Bits, size: number): void {
  let last = 8;
  let next = 8;
  for (let j = 0; j < size; j++) {
    if (next !== 0) {
      next = (last + bits.se() + 256) % 256;
    }
    if (next !== 0) {
      last = next;
    }
  }
}

/** The SPS fields the slice header needs to reach frame_num. */
function parseSps(bits: Bits): Sps {
  const profile = bits.u(8);
  // constraint flags, level_idc, seq_parameter_set_id
  bits.u(16);
  bits.ue();
  let separateColourPlane = false;
  if (HIGH_PROFILES.includes(profile)) {
    const chroma = bits.ue();
    if (chroma === 3) {
      separateColourPlane = bits.u(1) === 1;
    }
    // bit_depth_luma_minus8, bit_depth_chroma_minus8, qpprime_y_zero_transform_bypass_flag
    bits.ue();
    bits.ue();
    bits.u(1);
    if (bits.u(1) === 1) {
      const lists = chroma === 3 ? 12 : 8;
      for (let i = 0; i < lists; i++) {
        if (bits.u(1) === 1) {
          skipScalingList(bits, i < 6 ? 16 : 64);
        }
      }
    }
  }
  return { frameNumBits: bits.ue() + 4, separateColourPlane };
}

/** Follows the H.264 reference chain through frame_num, one encoded frame at a time, in decoding order. */
export class FrameInspector {
  private sps: Sps | null = null;
  /** frame_num of the last reference picture, null until the next IDR. */
  private lastRef: number | null = null;

  inspect(data: ArrayBuffer | Uint8Array): ChainState {
    const b = data instanceof Uint8Array ? data : new Uint8Array(data);
    try {
      const nals = headerNals(b);
      for (let i = 0; i < nals.length; i++) {
        const nal = nals[i];
        if (nal.type === SPS) {
          this.sps = parseSps(new Bits(b, nal.start, nal.end));
        } else if (SLICE_TYPES.includes(nal.type)) {
          return this.checkSlice(nal, new Bits(b, nal.start, Math.min(nal.end, nal.start + SLICE_HEADER_BYTES)));
        }
      }
    } catch {
      this.lastRef = null;
      return 'gap';
    }
    return 'noslice';
  }

  private checkSlice(nal: Nal, bits: Bits): ChainState {
    // A frame that does not start at macroblock 0 lost its head.
    if (bits.ue() !== 0) {
      this.lastRef = null;
      return 'gap';
    }
    if (nal.type === IDR) {
      this.lastRef = 0;
      return 'ok';
    }
    if (this.lastRef === null) {
      return 'wait';
    }
    if (this.sps === null) {
      // No SPS in band: nothing to check frame_num against.
      return 'ok';
    }
    // slice_type, pic_parameter_set_id, colour_plane_id
    bits.ue();
    bits.ue();
    if (this.sps.separateColourPlane) {
      bits.u(2);
    }
    const frameNum = bits.u(this.sps.frameNumBits);
    if (frameNum !== (this.lastRef + 1) % 2 ** this.sps.frameNumBits) {
      this.lastRef = null;
      return 'gap';
    }
    // Non-reference pictures share the next reference's frame_num.
    if (nal.refIdc !== 0) {
      this.lastRef = frameNum;
    }
    return 'ok';
  }
}

/**
 * The payload types in the video section of an SDP that carry H.264. The chain check applies to those frames only;
 * any other codec passes untouched.
 */
export function h264PayloadTypes(sdp: string): number[] {
  const types: number[] = [];
  sdp.split(/^m=/m).forEach((section) => {
    if (section.startsWith('video')) {
      const rtpmap = /^a=rtpmap:(\d+)\s+h264\/90000/gim;
      let match = rtpmap.exec(section);
      while (match !== null) {
        types.push(Number(match[1]));
        match = rtpmap.exec(section);
      }
    }
  });
  return types;
}
